import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, ClipboardCheck, ArrowDownToLine, ArrowUpFromLine, SlidersHorizontal, Trash2, Pencil, ArrowLeftRight, PackageCheck, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { fmtIDR, fmtNum, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Inventory() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const { user } = useAuth();
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const restricted = user && user.role !== "owner" && !!user.outlet_id;
  const [stock, setStock] = useState({ items: [], stock_value: 0 });
  const [movements, setMovements] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [opnameOpen, setOpnameOpen] = useState(false);
  const [editMv, setEditMv] = useState(null);
  const [mvForm, setMvForm] = useState({ qty: 0, unit_cost: 0, note: "" });
  const [moveForm, setMoveForm] = useState({ ingredient_id: "", type: "in", qty: 1, unit_cost: 0, note: "" });
  const [opnameRows, setOpnameRows] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [trForm, setTrForm] = useState({ ingredient_id: "", from_outlet_id: "", to_outlet_id: "", qty: 1, note: "" });
  const [transfers, setTransfers] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = outlet ? { outlet_id: outlet } : {};
      const [s, m, tr] = await Promise.all([api.get("/inventory/stock", { params }), api.get("/inventory/movements", { params }), api.get("/inventory/transfers", { params })]);
      setStock(s.data);
      setMovements(m.data);
      setTransfers(tr.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [outlet]);

  useEffect(() => { load(); }, [load]);

  const saveMovement = async () => {
    try {
      const payload = { ...moveForm, outlet_id: outlet || "" };
      if (payload.type === "in") {
        payload.unit_cost = payload.qty > 0 ? Math.round((Number(moveForm.unit_cost) / Number(moveForm.qty)) * 10000) / 10000 : 0;
      } else {
        payload.unit_cost = 0;
      }
      await api.post("/inventory/movement", payload);
      toast.success("OK");
      setMoveOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openOpname = () => {
    setOpnameRows(stock.items.map((i) => {
      const stk = outlet ? (i.outlet_stock ?? 0) : i.stock;
      return { ingredient_id: i.id, name: i.name, stock: stk, actual_stock: stk, unit: i.unit };
    }));
    setOpnameOpen(true);
  };

  const saveOpname = async () => {
    try {
      await api.post("/inventory/opname", { items: opnameRows.map((r) => ({ ingredient_id: r.ingredient_id, actual_stock: Number(r.actual_stock) })), outlet_id: outlet || "" });
      toast.success("OK");
      setOpnameOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteMovement = async (m) => {
    if (!window.confirm(m.type === "purchase" ? t("confirmDeletePurchase") : t("confirmDelete"))) return;
    try { await api.delete(`/inventory/movements/${m.id}`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const moveUnit = stock.items.find((i) => i.id === moveForm.ingredient_id)?.unit || "";

  const openTransfer = () => {
    setTrForm({ ingredient_id: "", from_outlet_id: outlet || "", to_outlet_id: "", qty: 1, note: "" });
    setTransferOpen(true);
  };

  const saveTransfer = async () => {
    try {
      await api.post("/inventory/transfer", trForm);
      toast.success(`OK — ${t("inTransit")}`);
      setTransferOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const receiveTransfer = async (tr) => {
    try { await api.post(`/inventory/transfers/${tr.id}/receive`); toast.success("OK — stok diterima"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const cancelTransfer = async (tr) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.post(`/inventory/transfers/${tr.id}/cancel`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const trStatusBadge = (s) => {
    const map = {
      in_transit: "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
      received: "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
      cancelled: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700",
    };
    const labels = { in_transit: t("inTransit"), received: t("received"), cancelled: t("cancelled") };
    return <span className={`border px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[s] || map.cancelled}`}>{labels[s] || s}</span>;
  };

  const trUnit = stock.items.find((i) => i.id === trForm.ingredient_id)?.unit || "";

  const outletNameOf = (oid) => (stock.outlets || []).find((o) => o.id === oid)?.name || "-";
  const stockRows = stock.items.flatMap((i) => {
    if (outlet) return [{ ing: i, outletId: outlet, qty: i.outlet_stock ?? 0 }];
    const entries = Object.entries(i.by_outlet || {}).filter(([, v]) => v !== 0);
    if (!entries.length) return [{ ing: i, outletId: "", qty: i.stock }];
    return entries.map(([oid, qty]) => ({ ing: i, outletId: oid, qty }));
  });

  const openEditMv = (m) => {
    setEditMv(m);
    setMvForm({ qty: Math.abs(m.qty), unit_cost: m.unit_cost || 0, note: m.note || "" });
  };

  const saveEditMv = async () => {
    try {
      await api.put(`/inventory/movements/${editMv.id}`, mvForm);
      toast.success("OK — stok & HPP dihitung ulang");
      setEditMv(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const typeBadge = (type) => {
    const map = {
      in: "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
      purchase: "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800",
      out: "bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800",
      sale: "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
      adjust: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
      opname: "bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800",
    };
    return <span className={`border px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[type] || map.adjust}`}>{type}</span>;
  };

  return (
    <div data-testid="inventory-page">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_inventory")}</h1>
        <div className="bg-card border border-border rounded-lg px-4 py-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground mr-2">{t("stockValue")}</span>
          <span className="font-mono font-bold" data-testid="inventory-stock-value">{fmtIDR(stock.stock_value)}</span>
        </div>
        <Button variant="outline" data-testid="transfer-btn" onClick={openTransfer}><ArrowLeftRight className="w-4 h-4 mr-1.5" />{t("transfer")}</Button>
        <Button variant="outline" data-testid="opname-btn" onClick={openOpname}><ClipboardCheck className="w-4 h-4 mr-1.5" />{t("opname")}</Button>
        <Button data-testid="movement-add-btn" onClick={() => setMoveOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1.5" />{t("stockIn")}/{t("stockOut")}</Button>
      </div>

      <Tabs defaultValue="stock">
        <TabsList data-testid="inventory-tabs">
          <TabsTrigger value="stock" data-testid="tab-stock">{t("stock")}</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">{t("movements")}</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">{t("transfers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="stock">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("name")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("category")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("outlet")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("stock")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Avg Cost</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("stockValue")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("status")}</th>
                </tr></thead>
                <tbody>
                  {stockRows.map((r) => {
                    const i = r.ing;
                    const avg = (i.avg_cost || 0) || (i.price || 0) || 0;
                    const rowLow = (i.min_stock || 0) > 0 && r.qty <= i.min_stock;
                    return (
                    <tr key={`${i.id}-${r.outletId || "x"}`} data-testid={`stock-row-${i.id}-${r.outletId || "x"}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{i.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{i.category_name || "-"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground" data-testid={`stock-outlet-${i.id}-${r.outletId || "x"}`}>{outletNameOf(r.outletId)}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtNum(r.qty)} {i.unit}</td>
                      <td className="px-4 py-3 text-right font-mono text-muted-foreground">{fmtIDR(avg)}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtIDR(r.qty * avg)}</td>
                      <td className="px-4 py-3 text-right">{rowLow ? <span className="bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800 px-2.5 py-0.5 rounded-full text-xs font-semibold">LOW</span> : <span className="bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 px-2.5 py-0.5 rounded-full text-xs font-semibold">OK</span>}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="movements">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("date")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("name")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("qty")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("cost")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</th>
                  {isAdmin && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("actions")}</th>}
                </tr></thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(m.created_at)}</td>
                      <td className="px-4 py-2.5">{m.ingredient_name}</td>
                      <td className="px-4 py-2.5">{typeBadge(m.type)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${m.qty >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.qty >= 0 ? "+" : ""}{fmtNum(m.qty)}{m.unit ? ` ${m.unit}` : ""}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{fmtIDR(m.unit_cost)}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{m.note || "-"}</td>
                      {isAdmin && (
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button data-testid={`movement-edit-${m.id}`} onClick={() => openEditMv(m)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`movement-delete-${m.id}`} onClick={() => deleteMovement(m)} className="p-1.5 rounded-md text-muted-foreground hover:text-rose-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="transfers">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs mt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">No</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("date")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("name")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("qty")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("fromOutlet")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("toOutlet")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("status")}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("actions")}</th>
                </tr></thead>
                <tbody>
                  {transfers.map((tr) => (
                    <tr key={tr.id} data-testid={`transfer-row-${tr.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2.5 font-mono text-xs">{tr.transfer_number}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(tr.created_at)}</td>
                      <td className="px-4 py-2.5">{tr.ingredient_name}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtNum(tr.qty)} {tr.unit}</td>
                      <td className="px-4 py-2.5 text-xs">{tr.from_name}</td>
                      <td className="px-4 py-2.5 text-xs">{tr.to_name}</td>
                      <td className="px-4 py-2.5">{trStatusBadge(tr.status)}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {tr.status === "in_transit" && (
                          <>
                            <Button size="sm" variant="outline" data-testid={`transfer-receive-${tr.id}`} onClick={() => receiveTransfer(tr)} className="mr-1.5"><PackageCheck className="w-3.5 h-3.5 mr-1" />{t("receive")}</Button>
                            <button data-testid={`transfer-cancel-${tr.id}`} onClick={() => cancelTransfer(tr)} className="p-1.5 text-muted-foreground hover:text-rose-600"><X className="w-4 h-4" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {transfers.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-md" data-testid="movement-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("stockIn")} / {t("stockOut")} / {t("adjustment")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</Label>
              <Select value={moveForm.ingredient_id} onValueChange={(v) => setMoveForm({ ...moveForm, ingredient_id: v })}>
                <SelectTrigger data-testid="movement-ingredient-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{stock.items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
              <Select value={moveForm.type} onValueChange={(v) => setMoveForm({ ...moveForm, type: v })}>
                <SelectTrigger data-testid="movement-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in"><span className="flex items-center gap-2"><ArrowDownToLine className="w-3.5 h-3.5" />{t("stockIn")}</span></SelectItem>
                  <SelectItem value="out"><span className="flex items-center gap-2"><ArrowUpFromLine className="w-3.5 h-3.5" />{t("stockOut")}</span></SelectItem>
                  <SelectItem value="adjust"><span className="flex items-center gap-2"><SlidersHorizontal className="w-3.5 h-3.5" />{t("adjustment")}</span></SelectItem>
                </SelectContent>
              </Select></div>
            {moveForm.type === "in" ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("unit")}</Label>
                    <div data-testid="movement-unit-display" className="mt-1.5 h-9 flex items-center px-3 rounded-md border border-border bg-muted/50 text-sm font-medium">{moveUnit || "-"}</div></div>
                  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("purchaseQty")}</Label>
                    <Input data-testid="movement-qty-input" type="number" value={moveForm.qty} onChange={(e) => setMoveForm({ ...moveForm, qty: Number(e.target.value) })} className="mt-1.5" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("purchasePrice")}</Label>
                    <Input data-testid="movement-cost-input" type="number" value={moveForm.unit_cost} onChange={(e) => setMoveForm({ ...moveForm, unit_cost: Number(e.target.value) })} className="mt-1.5" /></div>
                </div>
                <div className="text-xs bg-muted/50 border border-border rounded-lg px-3 py-2 text-muted-foreground" data-testid="movement-perunit-preview">
                  {t("pricePerUnit")}: <span className="font-mono font-semibold text-amber-600">{fmtIDR(moveForm.qty > 0 ? moveForm.unit_cost / moveForm.qty : 0)} / {moveUnit || "satuan"}</span>
                </div>
              </>
            ) : (
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("qty")}{moveUnit ? ` (${moveUnit})` : ""}</Label>
                <Input data-testid="movement-qty-input" type="number" value={moveForm.qty} onChange={(e) => setMoveForm({ ...moveForm, qty: Number(e.target.value) })} className="mt-1.5" /></div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
              <Input data-testid="movement-note-input" value={moveForm.note} onChange={(e) => setMoveForm({ ...moveForm, note: e.target.value })} className="mt-1.5" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMoveOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="movement-save-btn" onClick={saveMovement} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editMv} onOpenChange={() => setEditMv(null)}>
        <DialogContent className="max-w-md" data-testid="movement-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("edit")} Mutasi — {editMv?.ingredient_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">Type: <span className="font-mono font-semibold">{editMv?.type}</span> · {fmtDate(editMv?.created_at)}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("qty")}{editMv?.unit ? ` (${editMv.unit})` : ""}</Label>
                <Input data-testid="movement-edit-qty" type="number" value={mvForm.qty} onChange={(e) => setMvForm({ ...mvForm, qty: Number(e.target.value) })} className="mt-1.5" /></div>
              {(editMv?.type === "in" || editMv?.type === "purchase") && (
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("cost")}</Label>
                  <Input data-testid="movement-edit-cost" type="number" value={mvForm.unit_cost} onChange={(e) => setMvForm({ ...mvForm, unit_cost: Number(e.target.value) })} className="mt-1.5" /></div>
              )}
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
              <Input data-testid="movement-edit-note" value={mvForm.note} onChange={(e) => setMvForm({ ...mvForm, note: e.target.value })} className="mt-1.5" /></div>
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              Mengubah qty akan menyesuaikan stok; mengubah biaya akan menghitung ulang HPP resep & laporan.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditMv(null)}>{t("cancel")}</Button>
              <Button data-testid="movement-edit-save-btn" onClick={saveEditMv} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={opnameOpen} onOpenChange={setOpnameOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="opname-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("opname")}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Outlet: <span className="font-semibold text-foreground">{outlet ? outletNameOf(outlet) : t("allOutlets")}</span>{outlet ? " — stok sistem mengikuti bucket outlet ini" : " — stok global (semua outlet)"}</p>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground"><th className="text-left py-2">{t("name")}</th><th className="text-right">{t("stock")}</th><th className="text-right w-32">Actual</th></tr></thead>
            <tbody>
              {opnameRows.map((r, idx) => (
                <tr key={r.ingredient_id} className="border-b border-border last:border-0">
                  <td className="py-2">{r.name}</td>
                  <td className="text-right font-mono text-muted-foreground">{fmtNum(r.stock)} {r.unit}</td>
                  <td className="text-right py-1.5"><Input data-testid={`opname-actual-${idx}`} type="number" value={r.actual_stock} onChange={(e) => { const arr = [...opnameRows]; arr[idx] = { ...arr[idx], actual_stock: e.target.value }; setOpnameRows(arr); }} className="w-28 ml-auto text-right font-mono" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setOpnameOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="opname-save-btn" onClick={saveOpname} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("transfer")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</Label>
              <Select value={trForm.ingredient_id} onValueChange={(v) => setTrForm({ ...trForm, ingredient_id: v })}>
                <SelectTrigger data-testid="transfer-ingredient-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{stock.items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("fromOutlet")}</Label>
                <Select value={trForm.from_outlet_id} onValueChange={(v) => setTrForm({ ...trForm, from_outlet_id: v })}>
                  <SelectTrigger data-testid="transfer-from-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{(stock.outlets || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("toOutlet")}</Label>
                <Select value={trForm.to_outlet_id} onValueChange={(v) => setTrForm({ ...trForm, to_outlet_id: v })}>
                  <SelectTrigger data-testid="transfer-to-select" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                  <SelectContent>{(stock.outlets || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("qty")}{trUnit ? ` (${trUnit})` : ""}</Label>
              <Input data-testid="transfer-qty-input" type="number" value={trForm.qty} onChange={(e) => setTrForm({ ...trForm, qty: Number(e.target.value) })} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
              <Input data-testid="transfer-note-input" value={trForm.note} onChange={(e) => setTrForm({ ...trForm, note: e.target.value })} className="mt-1.5" /></div>
            <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              Stok langsung keluar dari outlet asal (status Dalam Perjalanan) dan baru masuk ke outlet tujuan setelah dikonfirmasi "Diterima". Total stok global & HPP tidak berubah.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTransferOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="transfer-save-btn" onClick={saveTransfer} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
